import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { INFO_PAGE_SLIDES_NUMBER, locale } from '../../../consts';
import { useSnackbar } from '../../../hooks/snackbar';
import { MediaBreakpoint, useBreakpointInfo } from '../../../hooks/useDeviceInfo';
import { ButtonLinkType, HeroBannerCarousel, HeroBannerSlide } from '../../HeroBannerCarousel';
import { Accordion, AccordionContent, AccordionHeader } from '../../shared/Accordion';
import { Button } from '../../shared/Button';
import { ContentPreview } from '../../shared/ContentPreview';
import { Heading } from '../../shared/Heading';
import { InnerHTML } from '../../shared/InnerHTML';
import { RouterLink } from '../../shared/Link';
import { GameSpecs } from '../GameSpecs';
import { Game, GameItemResponse } from '../games.model';
import { gameInfoConfig } from '../gameInfoConfig';
import { GameInfoCard } from './GameInfoCard';
import { GameInfoRegBonus } from './GameInfoRegBonus';
import { GameTags } from './GameTags';
import { GameFeatures } from '../GameFeatures';
import { useFetchGames } from '../../../hooks/useFetchGames';
import { storageRoute } from '../../../utils/storage';
import { useSelector } from 'react-redux';
import { selectDisabledGamesData } from '../../../state/game';
import { getRedirectUri } from '../../Authentification/helper';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import { useGameDetailsUrl } from '../../../hooks/games';
import { useSignIn } from '../../../hooks/auth';
import { useBrandedNavigate } from '../../../hooks/useBrandedNavigate';
import { ItvRecommendedGames } from '../../Itv/ItvRecommendedGames';
import './styles';

export type GameInfoProps = {
  /**
   * Game data to display info about
   */
  game: Game;
  /**
   * Whether user is currently logged in
   */
  isUserAuthenticated: boolean;
  /**
   * Button type used to trigger LowBalancePrompt Journey in HeroBannerCarousel
   */
  buttonLinkType?: ButtonLinkType;
  gameEnabled: boolean;
  /**
   * Whether game is initialized or not
   */
  isGameInitialised: boolean;
};

/**
 * Component to render game info
 * TODO: Had to use regular Link instead of RouterLink.  Not ideal.  Look into what can be done.
 */
export const GameInfo: FunctionComponent<GameInfoProps> = ({
  game,
  gameEnabled,
  isGameInitialised,
  isUserAuthenticated,
}) => {
  const { t } = useTranslations();
  const auth = useAuth();
  const signIn = useSignIn();
  const { navigate, registrationUrl } = useBrandedNavigate();
  const { addSnack } = useSnackbar();
  const { areGamesDisabled } = useSelector(selectDisabledGamesData);
  const isBelowMobileWidth = useBreakpointInfo([MediaBreakpoint.BELOW_MOBILE]);
  const isBelowDesktopWidth = useBreakpointInfo([MediaBreakpoint.BELOW_DESKTOP]);
  const {
    tags,
    rules,
    gameId,
    gameImage,
    displayName,
    bannerImage,
    categoryName,
    friendlyUrlName,
    longDescription,
    shortDescription,
  } = game;
  const { fetchGamesByTags } = useFetchGames({});
  const { buildGameDetailsUrl } = useGameDetailsUrl(game.brand, game.gameType);
  const rootClassName = 'game-info';

  const [similarGames, setSimilarGames] = useState<GameItemResponse[]>([]);

  const filteredGames = useMemo(
    () =>
      similarGames
        .filter((similarGame) => similarGame.translations?.[0]?.slug !== game.friendlyUrlName)
        .map((similarGame) => ({ game_id: similarGame })),
    [game.friendlyUrlName, similarGames.length]
  );

  const slideLinkProps = {
    buttonEnabled: !isUserAuthenticated || gameEnabled,
    buttonLabel: isUserAuthenticated ? t('buttons.play-here.uppercase') : t('buttons.join-here.uppercase'),
    buttonLink: isUserAuthenticated ? buildGameDetailsUrl(game.friendlyUrlName, 'real') : registrationUrl,
    routerLink: isUserAuthenticated,
  };
  const bannerBackground = bannerImage.imgUrl || gameImage.imgUrl;

  const slides: HeroBannerSlide[] = [
    {
      ...slideLinkProps,
      heading: displayName,
      subHeading: shortDescription || '',
      footer: '',
      imgSrc: bannerBackground,
      mobileImgSrc: bannerBackground,
      imgAlt: displayName,
      buttonLinkType: ButtonLinkType.GAME,
    },
  ];

  const featuresSection = <GameFeatures gameFeatures={game.features} heading={t('game.features.label')} />;
  const specsSection = <GameSpecs game={game} />;

  const showDescription = !gameInfoConfig.hideLongDescription && longDescription && longDescription.length;
  const showRules = !gameInfoConfig.hideRules && rules && rules.length;

  const fetchGames = async (): Promise<void> => {
    if (!game.tags?.length) {
      return;
    }

    for (const tag of tags || []) {
      const similarGamesResponse = await fetchGamesByTags({
        variables: {
          gameId,
          language: locale,
          count: INFO_PAGE_SLIDES_NUMBER,
          tag: tag?.name || '',
          category: categoryName || null,
        },
        onCompleted: (data) => {
          setSimilarGames((prevGames) => [...prevGames, ...data.game]);
        },
      }).then((resp) => resp.data?.game);

      if (similarGamesResponse?.length) break;
    }
  };

  useEffect(() => {
    gameId && fetchGames();
  }, [isGameInitialised, gameId]);

  const gameLaunch = async (event: React.MouseEvent | React.KeyboardEvent): Promise<void> => {
    event.preventDefault();
    const gameUrl = buildGameDetailsUrl(friendlyUrlName, 'real');

    if (isUserAuthenticated) {
      try {
        if (auth.user?.expired) {
          await auth.signinSilent();
        }

        navigate(gameUrl);

        storageRoute.set({
          value: buildGameDetailsUrl(friendlyUrlName),
        });
      } catch (error) {
        console.error('retrieveGameLaunchUrl error: ', error);
        addSnack({
          type: 'error',
          message: "Oops, something's gone wrong",
        });
      }

      return;
    }

    signIn({ redirectUri: getRedirectUri(gameUrl) });
  };

  return (
    <div className={rootClassName}>
      <div className="game-info__col-1">
        {/* Carousel */}
        {Boolean(bannerBackground) && (
          <HeroBannerCarousel
            isDisabledSlideContent={areGamesDisabled}
            className="game-info__banner game-info__col-1"
            slides={slides}
            autoplayInterval={6000}
            friendlyUrlName={friendlyUrlName}
            game={game}
            callEligiblePromos={false}
            isGameInfoPage
          />
        )}
        {/* Game tags */}
        <GameTags game={game} />
        {/* Unauthed users - shows register bonus banner, Authed users - shows Game play button on screens below 600px */}
        {isUserAuthenticated ? (
          isBelowMobileWidth &&
          (!gameEnabled ? (
            <Button type="button" size="large" className="button--full-width" disabled onClick={gameLaunch}>
              PLAY HERE
            </Button>
          ) : (
            <RouterLink
              buttonStyle={{ variant: 'primary', size: 'large' }}
              to="/real"
              className="button--full-width"
              onClick={gameLaunch}
            >
              PLAY HERE
            </RouterLink>
          ))
        ) : (
          <GameInfoRegBonus />
        )}
        {/* Game features. To be filled in EFE-931 */}
        {isBelowDesktopWidth && featuresSection}
        {/* Game description & rules */}
        {(showRules || showDescription) && (
          <GameInfoCard className="game-info__description">
            {showDescription && (
              <ContentPreview>
                <Heading level={5} className="game-info__card-heading">
                  {t('game.about.label')}
                </Heading>
                <InnerHTML text={longDescription as string} />
              </ContentPreview>
            )}

            {showRules && (
              <Accordion defaultExpanded={!isBelowDesktopWidth}>
                <AccordionHeader>{t('game.rules.label')}</AccordionHeader>
                <AccordionContent>
                  <InnerHTML text={rules} />
                </AccordionContent>
              </Accordion>
            )}
          </GameInfoCard>
        )}
        {/* Game specs */}
        {isBelowDesktopWidth && specsSection}
        {filteredGames.length > 0 && (
          <ItvRecommendedGames title={t('game.similar.label')} eligibleGames={filteredGames} />
        )}
      </div>

      {/* Second column containing features and specs, only shown > desktop width */}
      {!isBelowDesktopWidth && (
        <div className="game-info__col-2">
          {featuresSection}
          {specsSection}
        </div>
      )}
    </div>
  );
};
